import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Badge, Spinner, Table } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminOrgsPage.module.css";

interface OrgListItem {
  readonly id: string;
  readonly name: string;
  readonly plan: "trial" | "standard";
  readonly subscriptionStatus: string;
  readonly trialEndsAt: string;
  readonly suspendedAt: string | null;
  readonly cleanerCount: number;
  readonly monthlyEstimateGbp: number;
  readonly createdAt: string;
  readonly billingState: {
    readonly label: string;
    readonly trialDaysRemaining: number | null;
  };
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; orgs: readonly OrgListItem[] }
  | { kind: "error"; message: string };

type Filter = "all" | "trial_ending" | "past_due" | "suspended";

function labelVariant(label: string): "success" | "warning" | "danger" | "neutral" | "info" {
  if (label === "active") return "success";
  if (label === "trial_ending" || label === "past_due" || label === "trial_expired") return "warning";
  if (label === "suspended") return "danger";
  if (label === "cancelled") return "info";
  return "neutral";
}

export function AdminOrgsPage() {
  usePageTitle("Admin · Organisations");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    fetch("/api/admin/orgs")
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: { orgs: OrgListItem[] } };
        if (json.success && json.data) setState({ kind: "ready", orgs: json.data.orgs });
        else setState({ kind: "error", message: "Unexpected response" });
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, []);

  if (state.kind === "unauthorized") {
    return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;
  }
  if (state.kind === "loading") {
    return <AdminPage title="Organisations"><Spinner /></AdminPage>;
  }
  if (state.kind === "error") {
    return <AdminPage title="Organisations"><div className={styles.empty}>{state.message}</div></AdminPage>;
  }

  const counts = {
    all: state.orgs.length,
    trial_ending: state.orgs.filter((o) => o.billingState.label === "trial_ending").length,
    past_due: state.orgs.filter((o) => o.billingState.label === "past_due").length,
    suspended: state.orgs.filter((o) => o.billingState.label === "suspended").length,
  };
  const visibleOrgs = state.orgs.filter((o) => filter === "all" ? true : o.billingState.label === filter);

  return (
    <AdminPage title="Organisations" aside={`${state.orgs.length} total`}>
      <div className={styles.filters}>
        <Pill active={filter === "all"} onClick={() => setFilter("all")} label={`All ${counts.all}`} />
        <Pill active={filter === "trial_ending"} onClick={() => setFilter("trial_ending")} label={`Trial ending ${counts.trial_ending}`} />
        <Pill active={filter === "past_due"} onClick={() => setFilter("past_due")} label={`Past due ${counts.past_due}`} />
        <Pill active={filter === "suspended"} onClick={() => setFilter("suspended")} label={`Suspended ${counts.suspended}`} />
      </div>

      {visibleOrgs.length === 0 ? (
        <div className={styles.empty}>No organisations match this filter.</div>
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>State</Table.HeaderCell>
              <Table.HeaderCell>Cleaners</Table.HeaderCell>
              <Table.HeaderCell>Est. monthly</Table.HeaderCell>
              <Table.HeaderCell>Trial ends</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleOrgs.map((org) => (
              <Table.Row key={org.id}>
                <Table.Cell>
                  <Link to={`/admin/orgs/${org.id}`} className={styles.nameLink}>{org.name}</Link>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant={labelVariant(org.billingState.label)}>
                    {org.billingState.label.replace(/_/g, " ")}
                    {org.billingState.trialDaysRemaining !== null && org.billingState.label !== "trial_expired"
                      ? ` · ${org.billingState.trialDaysRemaining}d`
                      : ""}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{org.cleanerCount}</Table.Cell>
                <Table.Cell><span className={styles.money}>£{org.monthlyEstimateGbp.toFixed(2)}</span></Table.Cell>
                <Table.Cell>{new Date(org.trialEndsAt).toLocaleDateString("en-GB")}</Table.Cell>
                <Table.Cell>{new Date(org.createdAt).toLocaleDateString("en-GB")}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </AdminPage>
  );
}

function Pill({ active, onClick, label }: { readonly active: boolean; readonly onClick: () => void; readonly label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.pill} ${active ? styles.pillActive : ""}`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
